import { assertUnreachable } from '@kindo/universal';

import { EnvConfig, EnvVar, isValidEnvConfigValue } from '~/types';

// Ref: Properly typing window object https://www.totaltypescript.com/how-to-properly-type-window
declare const window: {
  __ENV: EnvConfig;
} & Window;

/**
 * Utility functions for accessing environment variables
 * Uses window.__ENV for runtime configuration injected via the env_config.ts file
 */

/**
 * Gets an environment variable from either:
 *  process.env, if kindo-managed kindo (injected by Vercel/Doppler at build/dev time), or
 *  window.__ENV, if self-managed kindo (injected via env_config.js script at runtime)
 *
 * @param key The environment variable key
 * @returns The environment variable value
 */
export function getEnvVar<K extends keyof EnvConfig>(key: K): EnvConfig[K] {
  // Check if we have window environment variables (set by the env_config.js script)
  const hasWindowEnv = typeof window !== 'undefined' && !!window.__ENV;

  // If the window env exists, we want to use that and ignore the process env,
  // since right now we don't have a case where we'd want to do otherwise
  const value = hasWindowEnv
    ? getValueFromWindowEnv(key, window.__ENV)
    : getValueFromProcessEnv(key);

  // For required environment variables, we must have a value
  if (!isValidEnvConfigValue(key, value)) {
    throw new Error(`Invalid env var ${String(key)}: ${value}`);
  }

  return value;
}

/**
 * Helper function to retrieve a value from window environment (set by the env_config.js script at runtime)
 */
function getValueFromWindowEnv<K extends keyof EnvConfig>(
  key: K,
  clientEnv: EnvConfig
): EnvConfig[K] {
  return clientEnv[key];
}

/**
 * Helper function to retrieve a value from process environment (set by Vercel/Doppler at build/dev time)
 */
function getValueFromProcessEnv<K extends keyof EnvConfig>(
  key: K
): string | undefined {
  switch (key) {
    case EnvVar.NEXT_PUBLIC_API_BASE_URL:
      return process.env.NEXT_PUBLIC_API_BASE_URL;
    case EnvVar.NEXT_PUBLIC_BASE_NEXT_URL:
      return process.env.NEXT_PUBLIC_BASE_NEXT_URL;
    case EnvVar.NEXT_PUBLIC_FILE_UPLOAD_BASE_URL:
      return process.env.NEXT_PUBLIC_FILE_UPLOAD_BASE_URL;
    case EnvVar.NEXT_PUBLIC_PUSHER_CLUSTER:
      return process.env.NEXT_PUBLIC_PUSHER_CLUSTER;
    case EnvVar.NEXT_PUBLIC_PUSHER_KEY:
      return process.env.NEXT_PUBLIC_PUSHER_KEY;
    case EnvVar.NEXT_PUBLIC_SLACK_CLIENT_ID:
      return process.env.NEXT_PUBLIC_SLACK_CLIENT_ID;
    case EnvVar.NEXT_PUBLIC_UNLEASH_FRONTEND_API_TOKEN:
      return process.env.NEXT_PUBLIC_UNLEASH_FRONTEND_API_TOKEN;
    case EnvVar.NEXT_PUBLIC_UNLEASH_FRONTEND_API_URL:
      return process.env.NEXT_PUBLIC_UNLEASH_FRONTEND_API_URL;
    default:
      return assertUnreachable(key);
  }
}
